import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { Pin } from 'common/constants';
import { int } from 'common/utils/helpers';
import { ClientReducerState } from 'client/reducers';
import css from './Line.scss';

const AXIS = {
	origin: 'origin',
	x: 'x',
	y: 'y',
} as const;

/**
 * @param currentPin {String}
 * @param style {Object} selected node style
 * @param style.position {String}
 * @param style.top {Number}
 * @param style.left {Number}
 * @param style.width {Number}
 * @param style.height {Number}
 * @param axis {String} axis on which the line should be drawn
 * @param node {Element} selected element node
 * @return {{y1: *, x1: *, y2: *, x2: *}}
 */
function getFixedLineCoords(currentPin, style, axis, node) {
	const { scrollLeft, scrollTop } = document.documentElement;
	const parent = node.parentNode.getBoundingClientRect();

	const fixed = style.position === 'fixed';
	const xMin = fixed ? 0 : parent.left + scrollLeft;
	const xMax = fixed ? window.innerWidth : xMin + parent.width;
	const yMin = fixed ? 0 : parent.top + scrollTop;
	const yMax = fixed ? window.innerHeight : yMin + parent.height;

	const x1 = {
		origin: () => {
			if ([Pin.lt, Pin.lb, Pin.l, Pin.none].includes(currentPin)) return xMin;
			if ([Pin.rt, Pin.rb, Pin.r].includes(currentPin)) return xMax;
			if ([Pin.t, Pin.b, Pin.c].includes(currentPin)) return xMin + (xMax - xMin) / 2;
			return 0;
		},
		x: () => {
			if ([Pin.lt, Pin.lb, Pin.l, Pin.t, Pin.b, Pin.none].includes(currentPin)) return xMin;
			if ([Pin.rt, Pin.rb, Pin.r].includes(currentPin)) return xMax;
			if ([Pin.c].includes(currentPin)) return xMin + (xMax - xMin) / 2;
			return 0;
		},
		y: () => int(style.left) + int(style.width) / 2,
	};

	const x2 = {
		origin: () => {
			if ([Pin.lt, Pin.lb, Pin.l, Pin.none].includes(currentPin)) return int(style.left);
			if ([Pin.rt, Pin.rb, Pin.r].includes(currentPin)) return int(style.left) + int(style.width);
			if ([Pin.t, Pin.b, Pin.c].includes(currentPin)) return int(style.left) + int(style.width / 2);
			return 0;
		},
		x: () => {
			if ([Pin.lt, Pin.lb, Pin.l, Pin.t, Pin.b, Pin.none].includes(currentPin)) return int(style.left);
			if ([Pin.rt, Pin.rb, Pin.r].includes(currentPin)) return int(style.left) + int(style.width);
			if ([Pin.c].includes(currentPin)) return int(style.left) + int(style.width / 2);
			return 0;
		},
		y: () => int(style.left) + int(style.width) / 2,
	};

	const y1 = {
		origin: () => {
			if ([Pin.lt, Pin.rt, Pin.t, Pin.none].includes(currentPin)) return yMin;
			if ([Pin.lb, Pin.rb, Pin.b].includes(currentPin)) return yMax;
			if ([Pin.l, Pin.r, Pin.c].includes(currentPin)) return yMin + (yMax - yMin) / 2;
			return 0;
		},
		x: () => int(style.top) + int(style.height) / 2,
		y: () => {
			if ([Pin.lt, Pin.l, Pin.t, Pin.rt, Pin.r, Pin.none].includes(currentPin)) return yMin;
			if ([Pin.lb, Pin.rb, Pin.b].includes(currentPin)) return yMax;
			if ([Pin.c].includes(currentPin)) return yMin + (yMax - yMin) / 2;
			return 0;
		},
	};

	const y2 = {
		origin: () => {
			if ([Pin.lt, Pin.rt, Pin.t, Pin.none].includes(currentPin)) return int(style.top);
			if ([Pin.lb, Pin.rb, Pin.b].includes(currentPin)) return int(style.top) + int(style.height);
			if ([Pin.l, Pin.r, Pin.c].includes(currentPin)) return int(style.top) + int(style.height / 2);
			return 0;
		},
		x: () => int(style.top) + int(style.height) / 2,
		y: () => {
			if ([Pin.lt, Pin.l, Pin.t, Pin.rt, Pin.r, Pin.none].includes(currentPin)) return int(style.top);
			if ([Pin.lb, Pin.rb, Pin.b].includes(currentPin)) return int(style.top) + int(style.height);
			if ([Pin.c].includes(currentPin)) return int(style.top) + int(style.height / 2);
			return 0;
		},
	};

	return {
		x1: x1[axis](),
		x2: x2[axis](),
		y1: y1[axis](),
		y2: y2[axis](),
		xMin,
		xMax,
		yMin,
		yMax,
		parentW: parent.width,
		parentH: parent.height,
	};
}

type Props = {
	pin: string;
	axis: (typeof AXIS)[keyof typeof AXIS];
	cardHeight: number;
	node: HTMLElement;
	selectionStyle: {
		top: number;
		left: number;
		width: number;
		height: number;
		position: string;
	};
};

const Line = ({ pin, selectionStyle, cardHeight, axis, node }: Props) => {
	if (!node || !node.parentNode) return null;
	const height = selectionStyle.position === 'fixed' ? '100%' : cardHeight;
	const coords = getFixedLineCoords(pin, selectionStyle, axis, node);
	const rectCoords = getFixedLineCoords(Pin.lt, selectionStyle, Line.axis.origin, node);

	return (
		<svg className={cn(css.line, css[`line-${selectionStyle.position}`])} style={{ height }}>
			<line x1={coords.x1} x2={coords.x2} y1={coords.y1} y2={coords.y2} />
			{selectionStyle.position !== 'fixed' && (
				<rect x={rectCoords.x1} y={rectCoords.y1} width={rectCoords.parentW} height={rectCoords.parentH} />
			)}
		</svg>
	);
};

Line.axis = AXIS;

export default connect(({ settings }: ClientReducerState) => ({ cardHeight: settings.cardHeight }))(Line);
